import { memo, useState } from 'react'
import SearchInput from '@/components/common/SearchInput'
import { FilterIcon, TriangleIcon } from '@/components/icons'

// Format enum mapping
const FORMAT_OPTIONS = [
    { value: 'IMAGE', label: 'Image' },
    { value: 'VIDEO', label: 'Video' },
    { value: 'CAROUSEL', label: 'Carousel' },
]

const FilterSection = memo(
    ({
        searchTerm,
        onSearchChange,
        filterFormats,
        onClearFormatFilters,
        onApplyFilters,
        selectionCount,
        onClearSelection,
        onSelectAll,
        showSelectAll = false,
    }) => {
        const [showFilters, setShowFilters] = useState(false)
        const [tempFilterFormats, setTempFilterFormats] = useState(new Set())

        const handleOpenFilters = () => {
            setTempFilterFormats(new Set(filterFormats))
            setShowFilters(true)
        }

        const handleTempFormatToggle = (format) => {
            const newTempFormats = new Set(tempFilterFormats)
            if (newTempFormats.has(format)) {
                newTempFormats.delete(format)
            } else {
                newTempFormats.add(format)
            }
            setTempFilterFormats(newTempFormats)
        }

        const handleApply = () => {
            onApplyFilters(tempFilterFormats)
            setShowFilters(false)
        }

        const handleCancel = () => {
            setTempFilterFormats(new Set())
            onClearFormatFilters()
            setShowFilters(false)
        }

        return (
            <>
                {/* Search and Filter Bar */}
                <div className="tw:flex tw:items-center tw:gap-4 tw:mb-1">
                    <div className="tw:flex-1">
                        <SearchInput
                            value={searchTerm}
                            onChange={onSearchChange}
                            placeholder="Search by name or tags"
                        />
                    </div>

                    <div className="tw:relative">
                        <button
                            onClick={handleOpenFilters}
                            className={`tw:border tw:border-[#2450BF] tw:px-3 tw:py-[6px] tw:font-medium tw:!rounded-[2px] tw:text-sm tw:cursor-pointer tw:overflow-hidden tw:flex tw:items-center tw:gap-2 ${
                                filterFormats.size > 0 ? 'tw:bg-[#2450BF59]' : 'tw:bg-transparent'
                            }`}
                        >
                            <FilterIcon />
                            <TriangleIcon />
                        </button>

                        {/* Filter Dropdown */}
                        {showFilters && (
                            <div className="tw:absolute tw:top-full tw:right-0 tw:mt-2 tw:bg-[#071727] tw:border tw:border-[#3B5168] tw:rounded-[2px] tw:p-2 tw:min-w-[240px] tw:z-[1000] tw:shadow-[0px_4px_24px_0px_#05111D40]">
                                <p className="tw:pt-1 tw:text-sm tw:text-[#899CBD] tw:!mb-2">Format</p>
                                {FORMAT_OPTIONS.map((option) => (
                                    <div key={option.value} className="tw:mb-1">
                                        <label className="tw:!flex tw:gap-2 tw:items-center tw:py-1 tw:cursor-pointer tw:rounded tw:text-sm">
                                            <input
                                                type="checkbox"
                                                checked={tempFilterFormats.has(option.value)}
                                                onChange={() => handleTempFormatToggle(option.value)}
                                            />
                                            {option.label}
                                        </label>
                                    </div>
                                ))}

                                {/* Cancel and Apply buttons */}
                                <div className="tw:flex tw:gap-2 tw:!mt-4">
                                    <button
                                        onClick={handleCancel}
                                        className="tw:h-[28px] tw:flex-1 tw:justify-center tw:bg-transparent tw:border tw:border-[#2450BF] tw:text-white tw:rounded-[2px] tw:!text-[13px] tw:cursor-pointer tw:font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleApply}
                                        className="tw:h-[28px] tw:flex-1 tw:justify-center tw:bg-[#2450BF] tw:border tw:border-[#2450BF] tw:text-white tw:rounded-[2px] tw:!text-[13px] tw:cursor-pointer tw:font-medium"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {showSelectAll && onSelectAll && (
                        <button
                            onClick={onSelectAll}
                            className="tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:text-white tw:px-3 tw:py-[6px] tw:font-medium tw:!rounded-[2px] tw:text-sm tw:cursor-pointer"
                        >
                            Select All
                        </button>
                    )}

                    {selectionCount > 0 && onClearSelection && (
                        <button
                            onClick={onClearSelection}
                            className="tw:bg-transparent tw:border tw:border-[#2450BF] tw:text-white tw:px-3 tw:py-[6px] tw:font-medium tw:!rounded-[2px] tw:text-sm tw:cursor-pointer"
                        >
                            Clear selection
                        </button>
                    )}
                </div>

                {/* Click outside to close filters */}
                {showFilters && (
                    <div
                        className="tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:bottom-0 tw:z-[999]"
                        onClick={() => setShowFilters(false)}
                    />
                )}
            </>
        )
    },
)

FilterSection.displayName = 'FilterSection'

export default FilterSection
